import React, { Component } from 'react';
import { kanaDictionary } from '../../data/kanaDictionary';
import { kanjiDictionary } from '../../data/kanjiDictionary';
import { quizSettings } from '../../data/quizSettings';
import { removeFromArray, arrayContains, shuffle, cartesianProduct } from '../../data/helperFuncs';
import './Question.scss';

class Question extends Component {
  state = {
    previousQuestion: [],
    previousAnswer: '',
    currentAnswer: '',
    currentQuestion: [],
    answerOptions: [],
    stageProgress: 0
  }

  // --- unified answer helpers ---
  getAnswersForKey(key) {
    return this.answerMap[key] || [];
  }

  getPrimaryAnswerForKey(key) {
    const arr = this.getAnswersForKey(key);
    return arr.length ? arr[0] : '';
  }

  // picks random symbols from askableKanaKeys, avoids duplicates by "primary answer"
  getRandomKanas(amount, include, exclude) {
    let randomized = this.askableKanaKeys.slice();

    if (exclude && exclude.length > 0) {
      randomized = removeFromArray(exclude, randomized);
    }

    if (include && include.length > 0) {
      randomized = removeFromArray(include, randomized);
      shuffle(randomized);
      randomized = randomized.slice(0, 20);

      let searchFor = this.getPrimaryAnswerForKey(include);
      randomized = randomized.filter(character => {
        return searchFor != this.getPrimaryAnswerForKey(character);
      });

      let temp = randomized.slice();
      randomized = randomized.filter(r => {
        let dupeFound = false;
        searchFor = this.getPrimaryAnswerForKey(r);
        temp.shift();
        temp.forEach(w => {
          if (this.getPrimaryAnswerForKey(w) == searchFor) dupeFound = true;
        });
        return !dupeFound;
      });

      randomized = randomized.slice(0, amount - 1);
      randomized.push(include);
      shuffle(randomized);
    } else {
      shuffle(randomized);
      randomized = randomized.slice(0, amount);
    }

    return randomized;
  }

  setNewQuestion() {
    if (this.props.stage != 4)
      this.currentQuestion = this.getRandomKanas(1, false, this.previousQuestion);
    else
      this.currentQuestion = this.getRandomKanas(3, false, this.previousQuestion);

    this.setState({ currentQuestion: this.currentQuestion });
    this.setAnswerOptions();
    this.setAllowedAnswers();
  }

  setAnswerOptions() {
    this.answerOptions = this.getRandomKanas(3, this.currentQuestion[0], false);
    this.setState({ answerOptions: this.answerOptions });
  }

  setAllowedAnswers() {
    this.allowedAnswers = [];

    if (this.props.stage == 1 || this.props.stage == 3) {
      // Question is symbol, answer is reading/romaji
      this.allowedAnswers = this.getAnswersForKey(this.currentQuestion[0]);
    }
    else if (this.props.stage == 2) {
      // Question is reading, answer is symbol
      this.allowedAnswers = this.currentQuestion;
    }
    else if (this.props.stage == 4) {
      const tempAllowed = [];
      this.currentQuestion.forEach(key => {
        tempAllowed.push(this.getAnswersForKey(key));
      });

      this.allowedAnswers = [];
      cartesianProduct(tempAllowed).forEach(answer => {
        this.allowedAnswers.push(answer.join(''));
      });
    }
  }

  getAnswerType() {
    if (this.props.stage == 2) return 'kana';
    return 'romaji';
  }

  getShowableQuestion() {
    if (this.getAnswerType() == 'kana') {
      // show a reading (primary) instead of symbol
      return this.getPrimaryAnswerForKey(this.state.currentQuestion[0]);
    }
    // show symbol
    return this.state.currentQuestion.join('');
  }

  isInAllowedAnswers(previousAnswer) {
    return arrayContains(previousAnswer, this.previousAllowedAnswers);
  }

  handleAnswer = answer => {
    if (this.props.stage <= 2) document.activeElement.blur();

    this.previousQuestion = this.currentQuestion;
    this.setState({ previousQuestion: this.previousQuestion });

    this.previousAnswer = answer;
    this.setState({ previousAnswer: this.previousAnswer });

    this.previousAllowedAnswers = this.allowedAnswers;

    if (this.isInAllowedAnswers(this.previousAnswer))
      this.stageProgress = this.stageProgress + 1;
    else
      this.stageProgress = this.stageProgress > 0 ? this.stageProgress - 1 : 0;

    this.setState({ stageProgress: this.stageProgress });

    if (this.stageProgress >= quizSettings.stageLength[this.props.stage] && !this.props.isLocked) {
      setTimeout(() => { this.props.handleStageUp() }, 300);
    } else {
      this.setNewQuestion();
    }
  }

  handleAnswerChange = e => {
    this.setState({ currentAnswer: e.target.value.replace(/\s+/g, '') });
  }

  handleSubmit = e => {
    e.preventDefault();
    if (this.state.currentAnswer != '') {
      this.handleAnswer(this.state.currentAnswer.toLowerCase());
      this.setState({ currentAnswer: '' });
    }
  }

  getPreviousResult() {
    if (this.previousQuestion == '') {
      return <div className="previous-result none">Let's go! Which character is this?</div>;
    }

    const questionDisplay = (this.props.stage == 2)
      ? this.getPrimaryAnswerForKey(this.previousQuestion[0])
      : this.previousQuestion.join('');

    const rightAnswer = questionDisplay + ' = ' + (this.previousAllowedAnswers && this.previousAllowedAnswers[0] ? this.previousAllowedAnswers[0] : '');

    if (this.isInAllowedAnswers(this.previousAnswer)) {
      return (
        <div className="previous-result correct" title="Correct answer!">
          <span className="pull-left glyphicon glyphicon-none"></span>
          {rightAnswer}
          <span className="pull-right glyphicon glyphicon-ok"></span>
        </div>
      );
    }

    return (
      <div className="previous-result wrong" title="Wrong answer!">
        <span className="pull-left glyphicon glyphicon-none"></span>
        {rightAnswer}
        <span className="pull-right glyphicon glyphicon-remove"></span>
      </div>
    );
  }

  // --- build askable pool from selected groups (kana + kanji) ---
  initializeCharacters() {
    this.askableKanaKeys = [];
    this.answerMap = {}; // symbol -> allowed answers array
    this.previousQuestion = '';
    this.previousAnswer = '';
    this.stageProgress = 0;

    // Kana
    Object.keys(kanaDictionary).forEach(whichKana => {
      Object.keys(kanaDictionary[whichKana]).forEach(groupName => {
        if (arrayContains(groupName, this.props.decidedGroups)) {
          const chars = kanaDictionary[whichKana][groupName].characters;
          Object.keys(chars).forEach(symbol => {
            if (!this.answerMap[symbol]) {
              this.askableKanaKeys.push(symbol);
              this.answerMap[symbol] = [];
            }
            // merge allowed answers (romaji array)
            chars[symbol].forEach(a => {
              if (this.answerMap[symbol].indexOf(a) < 0) this.answerMap[symbol].push(a);
            });
          });
        }
      });
    });

    // Kanji: selections are kan_groupX_kun / kan_groupX_on
    const kanji= kanjiDictionary['kanji'];
    Object.keys(kanji).forEach(groupName => {
      const group = kanji[groupName];
      const chars = (group && group.characters) ? group.characters : {};

      const kunId = groupName + '_kun';
      const onId = groupName + '_on';

      Object.keys(chars).forEach(symbol => {
        const entry = chars[symbol] || {};

        if (arrayContains(kunId, this.props.decidedGroups) && Array.isArray(entry.kun) && entry.kun.length > 0) {
          if (!this.answerMap[symbol]) {
            this.askableKanaKeys.push(symbol);
            this.answerMap[symbol] = [];
          }
          entry.kun.forEach(r => {
            if (this.answerMap[symbol].indexOf(r) < 0) this.answerMap[symbol].push(r);
          });
        }

        if (arrayContains(onId, this.props.decidedGroups) && Array.isArray(entry.on) && entry.on.length > 0) {
          if (!this.answerMap[symbol]) {
            this.askableKanaKeys.push(symbol);
            this.answerMap[symbol] = [];
          }
          entry.on.forEach(r => {
            if (this.answerMap[symbol].indexOf(r) < 0) this.answerMap[symbol].push(r);
          });
        }
      });
    });

    // de-dupe symbols list
    this.askableKanaKeys = this.askableKanaKeys.filter((v, i, a) => a.indexOf(v) === i);
  }

  componentWillMount() {
    this.initializeCharacters();
  }

  componentDidMount() {
    this.setNewQuestion();
  }

  getKanjiMeaning() {
    const symbols = this.state.currentQuestion || [];
    if (!symbols.length) return '';

    const kanji = kanjiDictionary['kanji'];
    let out = [];

    symbols.forEach(symbol => {
      for (const groupName in kanji) {
        const entry = kanji[groupName].characters[symbol] || {};

        if (entry && Array.isArray(entry.meanings) && entry.meanings.length) {
          out.push(entry.meanings.join(' · '));
          return;
        }
      }
    });

    return out.length ? out : '';
  }

  showKanjiMeaning() {
    return this.props.meaningsEnabled && this.props.stage < 4 && this.getKanjiMeaning();
  }

  render() {
    let btnClass = "btn btn-default answer-button";
    if ('ontouchstart' in window) btnClass += " no-hover";

    const stageProgressPercentage = Math.round((this.state.stageProgress / quizSettings.stageLength[this.props.stage]) * 100) + '%';
    const stageProgressPercentageStyle = { width: stageProgressPercentage };

    return (
      <div className="text-center question col-xs-12">
        {this.getPreviousResult()}
        <div className="big-character">{this.getShowableQuestion()}</div>
        { this.showKanjiMeaning() && <div className="meaning">{this.getKanjiMeaning()}</div> }
        <div className="answer-container">
          {
            this.props.stage < 3
              ? this.state.answerOptions.map((answer, idx) => {
                return (
                  <AnswerButton
                    answer={answer}
                    className={btnClass}
                    key={idx}
                    answertype={this.getAnswerType()}
                    handleAnswer={this.handleAnswer}
                    getPrimaryAnswer={(k) => this.getPrimaryAnswerForKey(k)}
                  />
                );
              })
              : (
                <div className="answer-form-container">
                  <form onSubmit={this.handleSubmit}>
                    <input
                      autoFocus
                      className="answer-input"
                      type="text"
                      value={this.state.currentAnswer}
                      onChange={this.handleAnswerChange}
                    />
                  </form>
                </div>
              )
          }
        </div>
        <div className="progress">
          <div
            className="progress-bar progress-bar-info"
            role="progressbar"
            aria-valuenow={this.state.stageProgress}
            aria-valuemin="0"
            aria-valuemax={quizSettings.stageLength[this.props.stage]}
            style={stageProgressPercentageStyle}
          >
            <span>Stage {this.props.stage} {this.props.isLocked ? ' (Locked)' : ''}</span>
          </div>
        </div>
      </div>
    );
  }
}

class AnswerButton extends Component {
  getShowableAnswer() {
    if (this.props.answertype == 'romaji') {
      return this.props.getPrimaryAnswer(this.props.answer);
    }
    return this.props.answer;
  }

  render() {
    return (
      <button className={this.props.className} onClick={() => this.props.handleAnswer(this.getShowableAnswer())}>
        {this.getShowableAnswer()}
      </button>
    );
  }
}

export default Question;
